import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router";
import { getApiUrl } from "../../api-clients";
import {
  ExamGradeApi,
  ExamGradeDto,
  TeacherApi,
  TeacherClassroomDto,
  TeacherDto,
} from "../../api-clients/api";
import Loading from "../Shared/Loading";
import NoResult from "../Shared/NoResult";
import LabelFieldValue from "../Shared/LabelFieldValue";
import FormButtons from "../Shared/FormButtons";
import ClassroomsDropDown from "../Shared/ClassroomsDropdown";
import ClearIcon from "@material-ui/icons/Clear";
import CheckIcon from "@material-ui/icons/Check";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import { SchoolContext } from "../../contexts/SchoolContext";
import { AppContext } from "../../contexts/AppContext";
import { dateToString } from "../../helper";
import { IDropdownOption } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { useRole } from "../../hooks/useRole";

export default function Teacher() {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const { teacherId }: any = useParams();
  const history = useHistory();
  const { accademicYear } = useContext(SchoolContext);
  const { openSnackbar } = useContext(AppContext);
  const [teacherApi] = useState<TeacherApi>(new TeacherApi(getApiUrl()));
  const [examGradeApi] = useState<ExamGradeApi>(new ExamGradeApi(getApiUrl()));
  const [teacher, setTeacher] = useState<TeacherDto | null>(null);
  const [teacherLoaded, setTeacherLoaded] = useState<boolean>(false);
  const [checkDelete, setCheckDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [classrooms, setClassrooms] = useState<TeacherClassroomDto[]>([]);
  const [classroomsLoading, setClassroomsLoading] = useState<boolean>(true);
  const [classroomToAssign, setClassroomToAssign] = useState<
    string | undefined
  >(undefined);
  const [assigning, setAssigning] = useState<boolean>(false);

  const [grades, setGrades] = useState<ExamGradeDto[]>([]);
  const [gradesLoading, setGradesLoading] = useState<boolean>(true);

  useEffect(() => {
    teacherApi
      .getById(teacherId)
      .then((res) => {
        setTeacher(res);
        setTeacherLoaded(true);
      })
      .catch(() => setTeacherLoaded(true));
  }, [teacherApi, teacherId]);

  const loadClassrooms = () => {
    if (accademicYear?.accademicYearId == null) return;
    setClassroomsLoading(true);
    teacherApi
      .classroomsGet(teacherId, accademicYear.accademicYearId)
      .then((res) => {
        setClassrooms(res);
        setClassroomsLoading(false);
      })
      .catch(() => setClassroomsLoading(false));
  };

  useEffect(() => {
    loadClassrooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherApi, teacherId, accademicYear]);

  useEffect(() => {
    setGradesLoading(true);
    examGradeApi
      .teacher(teacherId)
      .then((res) => {
        setGrades(res);
        setGradesLoading(false);
      })
      .catch(() => setGradesLoading(false));
  }, [examGradeApi, teacherId]);

  const deleteTeacher = () => {
    setDeleting(true);
    teacherApi
      .delete(teacherId)
      .then(() => {
        setDeleting(false);
        history.push("/teachers");
      })
      .catch(() => {
        setDeleting(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  const assignClassroom = () => {
    if (classroomToAssign == null || accademicYear?.accademicYearId == null)
      return;
    setAssigning(true);
    teacherApi
      .classroomsPost(teacherId, classroomToAssign, accademicYear.accademicYearId)
      .then(() => {
        setAssigning(false);
        setClassroomToAssign(undefined);
        loadClassrooms();
      })
      .catch(() => {
        setAssigning(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  const unassignClassroom = (classroomId: string) => {
    if (accademicYear?.accademicYearId == null) return;
    teacherApi
      .classroomsDelete(teacherId, classroomId, accademicYear.accademicYearId)
      .then(() => loadClassrooms())
      .catch(() => openSnackbar(t("common.errorOccurred"), "error"));
  };

  return (
    <Container maxWidth="lg">
      {teacherLoaded ? (
        teacher != null ? (
          <>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h5">{t("teachers.singular")}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item sm={6} xs={12}>
                        <LabelFieldValue
                          label={t("teachers.name")}
                          value={`${teacher.firstName ?? ""} ${
                            teacher.lastName ?? ""
                          }`}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <LabelFieldValue
                          label={t("teachers.email")}
                          value={teacher.email}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions disableSpacing>
                    {!isAdmin ? null : checkDelete ? (
                      <Grid container alignItems="center">
                        <Grid item xs={2}>
                          <Typography variant="subtitle1">
                            {t("common.confirmDelete")}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            aria-label={t("common.cancel")}
                            disabled={deleting}
                            onClick={() => setCheckDelete(false)}
                            color="inherit"
                          >
                            <ClearIcon />
                          </IconButton>
                          <IconButton
                            aria-label={t("common.confirm")}
                            disabled={deleting}
                            onClick={deleteTeacher}
                            color="secondary"
                          >
                            <CheckIcon />
                          </IconButton>
                          {deleting && (
                            <CircularProgress
                              size={24}
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                marginTop: -12,
                                marginLeft: -12,
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    ) : (
                      <FormButtons
                        confirmButtonVisible={false}
                        onDeleteClick={() => setCheckDelete(true)}
                        disabled={false}
                        cancelButtonVisible={false}
                        deleteButtonVisible={true}
                      />
                    )}
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title={t("teachers.classrooms")} />
                  <CardContent>
                    {!classroomsLoading ? (
                      classrooms.length > 0 ? (
                        <List>
                          {classrooms.map((c) => (
                            <div key={c.classroomId}>
                              <ListItem
                                style={{
                                  paddingTop: "0px",
                                  paddingBottom: "0px",
                                }}
                              >
                                <ListItemText primary={c.classroomName} />
                                {isAdmin && (
                                  <IconButton
                                    aria-label={t("teachers.unassign")}
                                    onClick={() =>
                                      c.classroomId &&
                                      unassignClassroom(c.classroomId)
                                    }
                                  >
                                    <RemoveCircleOutlineIcon />
                                  </IconButton>
                                )}
                              </ListItem>
                              <Divider />
                            </div>
                          ))}
                        </List>
                      ) : (
                        <NoResult />
                      )
                    ) : (
                      <Loading />
                    )}
                    {isAdmin && (
                      <Grid container spacing={1} style={{ marginTop: "8px" }}>
                        <Grid item xs={8}>
                          <ClassroomsDropDown
                            placeholder={t("teachers.selectClassroom")}
                            selectedKey={classroomToAssign}
                            onChange={(
                              event: React.FormEvent<HTMLDivElement>,
                              option?: IDropdownOption
                            ) => setClassroomToAssign(option?.key as string)}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormButtons
                            onConfirmClick={assignClassroom}
                            disabled={assigning || classroomToAssign == null}
                            cancelButtonVisible={false}
                            confirmIcon="Add"
                            confirmText={t("teachers.assign")}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title={t("teachers.examGrades")} />
                  <CardContent>
                    {!gradesLoading ? (
                      grades.length > 0 ? (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t("examGrades.student")}</TableCell>
                                <TableCell>{t("examGrades.subject")}</TableCell>
                                <TableCell align="right">
                                  {t("examGrades.grade")}
                                </TableCell>
                                <TableCell>{t("common.date")}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {grades.map((g) => (
                                <TableRow key={g.examGradeId}>
                                  <TableCell>
                                    {g.studentFirstName} {g.studentLastName}
                                  </TableCell>
                                  <TableCell>{g.subjectName}</TableCell>
                                  <TableCell align="right">
                                    {g.gradeText && g.gradeText.length > 0
                                      ? g.gradeText
                                      : g.gradeValue}
                                  </TableCell>
                                  <TableCell>{dateToString(g.date)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <NoResult />
                      )
                    ) : (
                      <Loading />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        ) : (
          <NoResult />
        )
      ) : (
        <Loading />
      )}
    </Container>
  );
}
